/** @jsxRuntime classic */
/** @jsx jsx */
import PropTypes from 'prop-types';
import { memo, forwardRef } from 'react';
import { jsx } from '@emotion/core';
import Axis from "./Axis";
import Labels from "./Labels";
import Grid from "./Grid";
import Points from "./Point";
// import Title from './Title';
import Path from "./Path";
import { randomString } from "../../../utils";
const LineChart = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  labelsVisible,
  labelsCharacterWidth,
  labelsCountY,
  data,
  viewBoxHeight,
  labelsOffsetY,
  viewBoxWidth,
  labelsOffsetX,
  labelsHeightX,
  // unitWidth,
  style,
  className,
  ...props
}, ref) => {
  // const colors = [
  //     '#FFD600',
  //     '#FFA800',
  //     '#FF7A00',
  //     '#FF0000',
  //     '#B61414',
  //     '#8FFF00',
  //     '#629000',
  //     '#019A5A',
  //     '#00C875',
  //     '#00FFD1',
  //     '#4200FF',
  //     '#0075FF',
  //     '#0095FF',
  //     '#00C2FF',
  //     '#00E0FF',
  //     '#8F00FF',
  //     '#BD00FF',
  //     '#FA00FF',
  //     '#FF00B8',
  //     '#FF3D71',
  // ];

  const dataSource = data.map((item, i) => ({
    ...item,
    x: i
  }));

  // console.log(dataSource);

  const UNIT_WIDTH = 1;
  const id = 'DGN-CUI-LineChart-' + randomString(6, {
    allowNumber: false,
    allowSymbol: false
  });
  const round = n => {
    return Math.round(n * 100) / 100;
  };
  const getMinX = () => {
    var _dataSource$;
    return (dataSource === null || dataSource === void 0 ? void 0 : dataSource.length) > 0 ? (_dataSource$ = dataSource[0]) === null || _dataSource$ === void 0 ? void 0 : _dataSource$.x : 0;
  };
  const getMinY = () => {
    return 0;
  };
  const getMaxX = () => {
    var _dataSource;
    return (dataSource === null || dataSource === void 0 ? void 0 : dataSource.length) > 0 ? (_dataSource = dataSource[(dataSource === null || dataSource === void 0 ? void 0 : dataSource.length) - 1]) === null || _dataSource === void 0 ? void 0 : _dataSource.x : 0;
  };
  const getMaxY = () => {
    var _dataSource$2;
    const yStep = labelsCountY > 0 ? labelsCountY : 1;
    const maxY = (dataSource === null || dataSource === void 0 ? void 0 : dataSource.length) > 0 ? dataSource.reduce((max, point) => point.quantity > max ? point.quantity : max, (_dataSource$2 = dataSource[0]) === null || _dataSource$2 === void 0 ? void 0 : _dataSource$2.quantity) : 0;
    return maxY ? Math.ceil(maxY / yStep) * yStep : yStep;
  };
  const getSvgX = x => {
    const minX = getMinX();
    const maxX = getMaxX();
    const paddingLeft = getLabelsYWidth();
    return round((x - minX) / (maxX + 1 - minX) * (Math.abs(viewBoxWidth) - paddingLeft));
  };
  const getSvgY = y => {
    const paddingBottom = labelsVisible ? labelsHeightX + labelsOffsetX : 0;
    const height = Math.abs(viewBoxHeight) - paddingBottom;
    const maxY = getMaxY();
    return round(height - y / maxY * height);
  };
  const getLabelsYWidth = () => {
    const maxY = getMaxY();
    return labelsVisible ? maxY.toString().length * labelsCharacterWidth + labelsOffsetY : 0;
  };
  const minX = getMinX();
  const maxX = getMaxX();
  const maxY = getMaxY();
  const minY = getMinY();
  const commonProps = {
    getX: getSvgX.bind(),
    getY: getSvgY.bind(),
    maxX: maxX,
    maxY: maxY,
    minX: minX,
    minY: minY,
    round: round,
    labelsWidthY: getLabelsYWidth(),
    unitWidth: UNIT_WIDTH
  };
  return jsx("svg", {
    id: id,
    className: ['DGN-CUI-LineChart', className].join(' ').trim().replace(/\s+/g, ' '),
    viewBox: `0 0 ${Math.abs(viewBoxWidth)} ${Math.abs(viewBoxHeight)}`,
    style: {
      display: 'block',
      overflow: 'visible',
      margin: `${getLabelsYWidth() * 2}`,
      ...style
    },
    ref: ref
  }, jsx(Axis, {
    ...props,
    ...commonProps
  }), jsx(Grid, {
    ...props,
    ...commonProps
  }), jsx(Labels, {
    data: dataSource,
    ...props,
    ...commonProps
  }), jsx(Path, {
    data: dataSource,
    viewBoxWidth: viewBoxWidth,
    ...props,
    ...commonProps
  }), jsx(Points, {
    data: dataSource,
    viewBoxWidth: viewBoxWidth,
    ...props,
    ...commonProps
  }));
}));
LineChart.defaultProps = {
  style: {},
  className: '',
  data: [],
  viewBoxHeight: 200,
  viewBoxWidth: 500,
  ...Axis.defaultProps,
  ...Labels.defaultProps,
  ...Points.defaultProps,
  ...Grid.defaultProps
};
LineChart.propTypes = {
  /** style inline for component */
  style: PropTypes.object,
  /** className for component */
  className: PropTypes.string,
  data: PropTypes.array.isRequired,
  /** drawn Ratio of viewBoxHeight entered by user, default is: 200px */
  viewBoxHeight: PropTypes.number,
  /** drawn Ratio of viewBoxWidth entered by user, default is: 500px */
  viewBoxWidth: PropTypes.number,
  /** the axisColor change color */
  axisColor: PropTypes.string,
  /** the axisVisible Hide or show the Axis axis */
  axisVisible: PropTypes.bool,
  /** change style show of label ['', 'italic'], default: 'italic' */
  labelStyle: PropTypes.string,
  /** change  Line grid display style ['full' , 'horizontal', 'none'], default: 'full'*/
  GridLines: PropTypes.string,
  /** show data information when hovering in the area containing the column */
  pointsIsHoverOnZone: PropTypes.bool,
  /** show or hidden point */
  pointsVisible: PropTypes.bool,
  /** change the inner color of the point button  */
  pointsColor: PropTypes.string,
  /** change the radius of the point is created */
  pointsRadius: PropTypes.number,
  /** the thickness of the line to be drawn */
  STROKE: PropTypes.number,
  /** shows the line to be drawn */
  PathVisible: PropTypes.bool,
  /** The color of the line drawn is the default color according to the data returned from the API, otherwise it will be generated by the design. */
  pathColor: PropTypes.string,
  /** show or hidden the line to be drawn smooth */
  Smooth: PropTypes.bool,
  /** the direction to display tootip */
  pointHoverAlignment: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  ...Axis.propTypes,
  ...Labels.propTypes,
  ...Points.propTypes,
  ...Grid.propTypes
};
export default LineChart;